import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardApi } from '../services/api';
import { LoadingPage } from '../components/shared/LoadingSpinner';
import { StatStrip } from '../components/shared/StatStrip';
import {
  Users, Database, AlertTriangle, TrendingUp, CheckCircle, Server, Zap, RefreshCw, Mail, Package, Clock, Truck, FileText, Hash, UserPlus
} from 'lucide-react';
import type { AdminDashboard as AdminDashboardType } from '../types';

interface AdminStatsData {
  totalCounts: {
    total_carriers: number;
    carriers_with_inspections: number;
    total_inspections: number;
    total_violations: number;
    total_crashes: number;
    total_unique_vins: number;
    carriers_with_violations: number;
    carriers_with_crashes: number;
  };
  dataFreshness: {
    latest_inspection_date: string;
    latest_crash_date: string;
    last_inspection_import: string;
    last_crash_import: string;
    inspections_imported_24h: number;
    violations_imported_24h: number;
    crashes_imported_24h: number;
  };
  recentActivity: {
    new_carriers_last_week: number;
    recent_inspections_30_days: number;
    recent_crashes_30_days: number;
    recent_violations_30_days: number;
  };
}

export const AdminDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState<AdminDashboardType | null>(null);
  const [adminStats, setAdminStats] = useState<AdminStatsData | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await dashboardApi.getAdminDashboard();
        setDashboard(data);
      } catch (error) {
        console.error('Error fetching admin dashboard:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchAdminStats = async () => {
      try {
        const response = await fetch('/api/admin/stats', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        });
        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            setAdminStats(result.data);
          }
        }
      } catch (error) {
        console.error('Error fetching admin stats:', error);
      } finally {
        setStatsLoading(false);
      }
    };

    fetchData();
    fetchAdminStats();
  }, []);

  if (loading) {
    return <LoadingPage message="Loading admin dashboard..." />;
  }

  if (!dashboard) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container-spacious">
          <div className="card-modern text-center p-8">
            <AlertTriangle className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--color-danger)' }} />
            <h3 className="heading-3 mb-2">Failed to load dashboard</h3>
            <p className="text-muted">Please try refreshing the page or contact support.</p>
          </div>
        </div>
      </div>
    );
  }

  // Calculate system health percentage
  const systemHealthScore =
    (dashboard.systemHealth.apiStatus === 'healthy' ? 25 : 0) +
    (dashboard.systemHealth.databaseStatus === 'connected' ? 25 : 0) +
    (dashboard.systemHealth.cacheHitRate > 0.8 ? 25 : dashboard.systemHealth.cacheHitRate > 0.5 ? 15 : 5) +
    (dashboard.systemHealth.errorRate < 0.01 ? 25 : dashboard.systemHealth.errorRate < 0.05 ? 15 : 5);

  return (
    <div className="min-h-screen bg-gray-50">

      <div className="container-spacious">
        {/* Header */}
        <div className="card-modern fade-in-up section-spacing" style={{ animationDelay: '0.1s' }}>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="heading-1 mb-1">Admin Dashboard</h1>
              <p className="text-muted">Platform overview and system management</p>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-muted text-sm">
                Last updated: {new Date().toLocaleTimeString()}
              </span>
              <button
                onClick={() => window.location.reload()}
                className="btn-secondary p-2"
              >
                <RefreshCw className="w-4 h-4" style={{ color: 'var(--color-gray-600)' }} />
              </button>
            </div>
          </div>
        </div>

        {/* System Health */}
        <div className="card-modern fade-in-up section-spacing" style={{ animationDelay: '0.2s' }}>
          <div className="flex items-center justify-between mb-6">
            <h3 className="heading-3 flex items-center gap-2 mb-0">
              <Server style={{ color: 'var(--color-primary)' }} size={20} />
              System Health
            </h3>
            <span className="badge-modern badge-success">
              {systemHealthScore}% Healthy
            </span>
          </div>

          <StatStrip cells={[
            {
              key: 'api', label: 'API Status',
              value: dashboard.systemHealth.apiStatus === 'healthy' ? 'Operational' : 'Issues',
              tone: dashboard.systemHealth.apiStatus === 'healthy' ? 'ok' : 'risk',
            },
            {
              key: 'db', label: 'Database',
              value: dashboard.systemHealth.databaseStatus === 'connected' ? 'Connected' : 'Disconnected',
              tone: dashboard.systemHealth.databaseStatus === 'connected' ? 'ok' : 'risk',
            },
            {
              key: 'cache', label: 'Cache Hit Rate',
              value: `${(dashboard.systemHealth.cacheHitRate * 100).toFixed(1)}%`,
              note: dashboard.systemHealth.cacheHitRate > 0.8 ? 'Good' : dashboard.systemHealth.cacheHitRate > 0.5 ? 'Fair' : 'Low',
              tone: dashboard.systemHealth.cacheHitRate > 0.8 ? 'ok' : dashboard.systemHealth.cacheHitRate > 0.5 ? 'warn' : 'risk',
            },
            {
              key: 'err', label: 'Error Rate',
              value: `${(dashboard.systemHealth.errorRate * 100).toFixed(2)}%`,
              note: dashboard.systemHealth.errorRate < 0.01 ? 'Excellent' : dashboard.systemHealth.errorRate < 0.05 ? 'Acceptable' : 'High',
              tone: dashboard.systemHealth.errorRate < 0.01 ? 'ok' : dashboard.systemHealth.errorRate < 0.05 ? 'warn' : 'risk',
            },
          ]} />
        </div>

        {/* Platform Statistics */}
        <div className="card-modern fade-in-up section-spacing" style={{ animationDelay: '0.3s' }}>
          <h3 className="heading-3 flex items-center gap-2 mb-6">
            <Database style={{ color: 'var(--color-primary)' }} size={20} />
            Platform Statistics
          </h3>
          <StatStrip cells={[
            {
              key: 'users', label: 'Total Users',
              value: dashboard.platformStats.totalUsers.toLocaleString(),
              note: `${dashboard.platformStats.totalCarriers} Carriers · ${dashboard.platformStats.totalBrokers} Brokers`,
            },
            {
              key: 'points', label: 'Data Points',
              value: dashboard.platformStats.totalDataPoints.toLocaleString(),
              note: '78% analyzed',
            },
            {
              key: 'verify', label: 'Verification Rate',
              value: `${(dashboard.platformStats.crossValidationRate * 100).toFixed(1)}%`,
              note: 'Cross-validated',
              tone: 'ok',
            },
            {
              key: 'credits', label: 'Credits Issued',
              value: dashboard.platformStats.creditsIssued.toLocaleString(),
            },
          ]} />
        </div>

        {/* User Distribution */}
        <div className="card-modern fade-in-up section-spacing" style={{ animationDelay: '0.4s' }}>
          <h3 className="heading-3 flex items-center gap-2 mb-6">
            <Users style={{ color: 'var(--color-primary)' }} size={20} />
            User Distribution
          </h3>

          <div className="grid-spacious grid-cols-[repeat(auto-fit,minmax(200px,1fr))]">
            <StatStrip cells={[
              { key: 'carriers', label: 'Carriers', value: dashboard.platformStats.totalCarriers.toLocaleString(), note: 'Motor carrier accounts' },
              { key: 'brokers', label: 'Brokers', value: dashboard.platformStats.totalBrokers.toLocaleString(), note: 'Freight broker accounts' },
              { key: 'shippers', label: 'Shippers', value: dashboard.platformStats.totalShippers.toLocaleString(), note: 'Shipper accounts' },
            ]} />
          </div>
        </div>

        {/* Platform Data Statistics */}
        <div className="card-modern fade-in-up" style={{ animationDelay: '0.45s' }}>
          <h3 className="heading-3 flex items-center gap-2 mb-6">
            <Database style={{ color: 'var(--color-primary)' }} size={20} />
            Platform Data Statistics
          </h3>

          {statsLoading && (
            <div className="text-center py-8">
              <RefreshCw className="w-8 h-8 mx-auto mb-3 text-blue-500 animate-spin" />
              <p className="text-gray-600">Loading platform statistics...</p>
              <p className="text-sm text-gray-500 mt-1">(This may take up to 45 seconds on first load)</p>
            </div>
          )}

          {!statsLoading && !adminStats && (
            <div className="text-center py-8">
              <AlertTriangle className="w-8 h-8 mx-auto mb-3 text-yellow-500" />
              <p className="text-gray-600">Failed to load platform statistics</p>
            </div>
          )}

          {adminStats && !statsLoading && (
            <div>

            {/* Total Records Grid */}
            <div className="mb-6">
              <StatStrip cells={[
                {
                  key: 'carriers', label: 'Total Carriers',
                  value: adminStats.totalCounts.total_carriers.toLocaleString(),
                  note: `${adminStats.totalCounts.carriers_with_inspections.toLocaleString()} with inspections`,
                },
                {
                  key: 'inspections', label: 'Inspections',
                  value: adminStats.totalCounts.total_inspections.toLocaleString(),
                  note: `${adminStats.totalCounts.total_violations.toLocaleString()} violations`,
                  tone: 'warn',
                },
                {
                  key: 'crashes', label: 'Crashes',
                  value: adminStats.totalCounts.total_crashes.toLocaleString(),
                  note: `${adminStats.totalCounts.carriers_with_crashes.toLocaleString()} carriers`,
                  tone: 'risk',
                },
                {
                  key: 'vins', label: 'Unique VINs',
                  value: adminStats.totalCounts.total_unique_vins.toLocaleString(),
                  note: `${adminStats.totalCounts.carriers_with_violations.toLocaleString()} carriers w/ violations`,
                },
              ]} />
            </div>

            {/* Data Freshness */}
            <div className="border-t border-gray-200 pt-6 mb-6">
              <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Clock size={18} style={{ color: 'var(--color-success)' }} />
                Data Freshness (Last 24h)
              </h4>
              <div className="grid-spacious grid-cols-[repeat(auto-fit,minmax(150px,1fr))]">
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <div className="text-xl font-bold text-green-900">
                    +{adminStats.dataFreshness.inspections_imported_24h.toLocaleString()}
                  </div>
                  <div className="text-xs text-green-700 mt-1">Inspections</div>
                </div>
                <div className="text-center p-3 bg-orange-50 rounded-lg">
                  <div className="text-xl font-bold text-orange-900">
                    +{adminStats.dataFreshness.violations_imported_24h.toLocaleString()}
                  </div>
                  <div className="text-xs text-orange-700 mt-1">Violations</div>
                </div>
                <div className="text-center p-3 bg-red-50 rounded-lg">
                  <div className="text-xl font-bold text-red-900">
                    +{adminStats.dataFreshness.crashes_imported_24h.toLocaleString()}
                  </div>
                  <div className="text-xs text-red-700 mt-1">Crashes</div>
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="border-t border-gray-200 pt-6">
              <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <TrendingUp size={18} style={{ color: 'var(--color-primary)' }} />
                30-Day Activity
              </h4>
              <div className="grid-spacious grid-cols-[repeat(auto-fit,minmax(140px,1fr))]">
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <div className="text-xl font-bold text-blue-900">
                    {adminStats.recentActivity.recent_inspections_30_days.toLocaleString()}
                  </div>
                  <div className="text-xs text-blue-700 mt-1">Inspections</div>
                </div>
                <div className="text-center p-3 bg-orange-50 rounded-lg">
                  <div className="text-xl font-bold text-orange-900">
                    {adminStats.recentActivity.recent_violations_30_days.toLocaleString()}
                  </div>
                  <div className="text-xs text-orange-700 mt-1">Violations</div>
                </div>
                <div className="text-center p-3 bg-red-50 rounded-lg">
                  <div className="text-xl font-bold text-red-900">
                    {adminStats.recentActivity.recent_crashes_30_days.toLocaleString()}
                  </div>
                  <div className="text-xs text-red-700 mt-1">Crashes</div>
                </div>
                <div className="text-center p-3 bg-purple-50 rounded-lg">
                  <div className="text-xl font-bold text-purple-900">
                    {adminStats.recentActivity.new_carriers_last_week.toLocaleString()}
                  </div>
                  <div className="text-xs text-purple-700 mt-1">New Carriers (7d)</div>
                </div>
              </div>
            </div>
            </div>
          )}
        </div>

        {/* Admin Settings */}
        <div className="card-modern fade-in-up" style={{ animationDelay: '0.5s' }}>
          <h3 className="heading-3 flex items-center gap-2 mb-6">
            <Server style={{ color: 'var(--color-primary)' }} size={20} />
            Admin Settings
          </h3>

          <div className="flex flex-col gap-4">
            <button
              onClick={() => navigate('/admin/smtp-settings')}
              className="card-modern-compact flex items-center gap-4 w-full text-left p-4 cursor-pointer border border-gray-200 bg-white transition-all hover:bg-gray-50 hover:border-gray-300"
            >
              <div className="flex items-center justify-center w-10 h-10 rounded-lg" style={{ background: 'var(--color-primary-light)' }}>
                <Mail style={{ color: 'var(--color-primary)' }} size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">SMTP Settings</h3>
                <p className="text-muted text-sm">Configure email server</p>
              </div>
            </button>

            <button
              onClick={() => navigate('/admin/registration-requests')}
              className="card-modern-compact flex items-center gap-4 w-full text-left p-4 cursor-pointer border border-gray-200 bg-white transition-all hover:bg-gray-50 hover:border-gray-300"
            >
              <div className="flex items-center justify-center w-10 h-10 rounded-lg" style={{ background: 'var(--color-warning-light)' }}>
                <UserPlus style={{ color: 'var(--color-warning)' }} size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Registration Requests</h3>
                <p className="text-muted text-sm">Review and approve new user registrations</p>
              </div>
            </button>

            <button
              onClick={() => navigate('/system-metrics')}
              className="card-modern-compact flex items-center gap-4 w-full text-left p-4 cursor-pointer border border-gray-200 bg-white transition-all hover:bg-gray-50 hover:border-gray-300"
            >
              <div className="flex items-center justify-center w-10 h-10 rounded-lg" style={{ background: 'var(--color-success-light)' }}>
                <TrendingUp style={{ color: 'var(--color-success)' }} size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Model Metrics</h3>
                <p className="text-muted text-sm">Bayesian model performance & accuracy</p>
              </div>
            </button>

            <button
              onClick={() => navigate('/system-metrics')}
              className="card-modern-compact flex items-center gap-4 w-full text-left p-4 cursor-pointer border border-gray-200 bg-white transition-all hover:bg-gray-50 hover:border-gray-300"
            >
              <div className="flex items-center justify-center w-10 h-10 rounded-lg" style={{ background: 'var(--color-info-light)' }}>
                <Server style={{ color: 'var(--color-info)' }} size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">System Health</h3>
                <p className="text-muted text-sm">Database, API performance & monitoring</p>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
